export type CrearAnuncioModo = 'rapido' | 'personalizado';
export type CrearAnuncioObjetivo = 'mostrar' | 'oferta' | 'lanzamiento' | 'evento';
export type CrearAnuncioComposicion = 'modelo' | 'producto';
export type CrearAnuncioTipo = 'imagen' | 'video';
export type CrearAnuncioFormato = 'post' | 'story' | 'square' | 'portrait';
export type CrearAnuncioEstilo = 'minimalista' | 'creativo' | 'automatico';
export type CrearAnuncioBackgroundMode = 'ia' | 'blanco' | 'elegido' | 'descripcion' | 'guardado';
export type CrearAnuncioModeloMode = 'ia' | 'guardados' | 'personalizado';
export type CrearAnuncioActiveTab = 'automatico' | 'personalizado' | 'creativo';

export interface CrearAnuncioBackgroundState {
  mode?: CrearAnuncioBackgroundMode;
  id?: number | null;
  url?: string;
  text?: string;
  fileName?: string;
}

export interface CrearAnuncioCompositionState {
  enabled: boolean;
  selectedCompId: number | null;
  selectedCompUrl?: string;
  compositionText?: string;
}

export interface CrearAnuncioModeloState {
  mode: CrearAnuncioModeloMode;
  guardadoId?: string;
  guardadoNombre?: string;
  genero?: string;
  edad?: string;
  tipoCuerpo?: string;
  tonoPiel?: string;
  colorPelo?: string;
  largoPelo?: string;
  descripcion?: string;
}

export interface CrearAnuncioLastState {
  timestamp: number;
  modo?: CrearAnuncioModo;
  activeTab?: CrearAnuncioActiveTab;
  selectedVariantIds?: string[];
  objetivo?: CrearAnuncioObjetivo;
  detallesOferta?: string;
  composicion?: CrearAnuncioComposicion;
  tipo?: CrearAnuncioTipo;
  formato?: CrearAnuncioFormato;
  cantidadImagenes?: number;
  estilo?: CrearAnuncioEstilo;
  background?: CrearAnuncioBackgroundState;
  composition?: CrearAnuncioCompositionState;
  modelo?: CrearAnuncioModeloState;
  modeloEnabled?: boolean;
  multiPoseToggle?: boolean;
  pose?: string;
  plano?: string;
  vistaProducto?: string;
  rubro?: string;
  modoPro?: boolean;
  creativoPrompt?: string;
}

export interface CrearAnuncioStateUpdate {
  modo?: CrearAnuncioModo | null;
  activeTab?: CrearAnuncioActiveTab | null;
  selectedVariantIds?: string[] | null;
  objetivo?: CrearAnuncioObjetivo | null;
  detallesOferta?: string | null;
  composicion?: CrearAnuncioComposicion | null;
  tipo?: CrearAnuncioTipo | null;
  formato?: CrearAnuncioFormato | null;
  cantidadImagenes?: number | null;
  estilo?: CrearAnuncioEstilo | null;
  pose?: string | null;
  plano?: string | null;
  vistaProducto?: string | null;
  modeloEnabled?: boolean | null;
  multiPoseToggle?: boolean | null;
  background?: CrearAnuncioBackgroundState | null;
  composition?: CrearAnuncioCompositionState | null;
  modelo?: CrearAnuncioModeloState | null;
  rubro?: string | null;
  modoPro?: boolean | null;
  creativoPrompt?: string | null;
}

const STORAGE_KEY = 'atlas:crear-anuncio:last-state';
const TTL_MS = 1000 * 60 * 60 * 6; // 6 horas

const isBrowser = typeof window !== 'undefined';

const mergeNestedState = <T extends object | undefined | null>(
  previous: T,
  incoming: T | null | undefined
): T | undefined => {
  if (incoming === null) {
    return undefined;
  }
  if (incoming === undefined) {
    return previous === undefined ? undefined : { ...previous } as T;
  }
  return { ...(previous || {}), ...incoming } as T;
};

export const loadLastCrearAnuncioState = (): CrearAnuncioLastState | null => {
  if (!isBrowser) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CrearAnuncioLastState | null;
    if (!parsed) return null;
    if (!parsed.timestamp || Date.now() - parsed.timestamp > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    // Sanitize variant ids to always be UUID strings
    if (Array.isArray(parsed.selectedVariantIds)) {
      parsed.selectedVariantIds = parsed.selectedVariantIds
        .map((v: any) => String(v))
        .map((s) => s.trim())
        .filter((s) => !!s && s !== '0');
    }
    return parsed;
  } catch (error) {
    console.warn('No se pudo leer el estado previo de Crear anuncio:', error);
    return null;
  }
};

const persistState = (state: CrearAnuncioLastState | null) => {
  if (!isBrowser) return;
  try {
    if (!state) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    const payload: CrearAnuncioLastState = {
      ...state,
      timestamp: Date.now(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('No se pudo persistir el estado de Crear anuncio:', error);
  }
};

export const mergeLastCrearAnuncioState = (
  update: CrearAnuncioStateUpdate,
): CrearAnuncioLastState | null => {
  const previous = loadLastCrearAnuncioState();

  try {
    const base: CrearAnuncioLastState = previous ? { ...previous } : { timestamp: Date.now() };

    if (update.background !== undefined) {
      base.background = mergeNestedState(previous?.background, update.background);
    }
    if (update.composition !== undefined) {
      base.composition = mergeNestedState(previous?.composition, update.composition);
    }
    if (update.modelo !== undefined) {
      base.modelo = mergeNestedState(previous?.modelo, update.modelo);
    }

    const applySimpleValue = <K extends keyof CrearAnuncioStateUpdate & keyof CrearAnuncioLastState>(
      key: K,
    ) => {
      const value = update[key];
      if (value === undefined) return;
      if (value === null) {
        delete base[key];
      } else {
        base[key] = value as CrearAnuncioLastState[K];
      }
    };

    applySimpleValue('modo');
    applySimpleValue('activeTab');
    applySimpleValue('selectedVariantIds');
    applySimpleValue('objetivo');
    applySimpleValue('detallesOferta');
    applySimpleValue('composicion');
    applySimpleValue('tipo');
    applySimpleValue('formato');
    applySimpleValue('cantidadImagenes');
    applySimpleValue('estilo');
    applySimpleValue('pose');
    applySimpleValue('plano');
    applySimpleValue('vistaProducto');
    applySimpleValue('modeloEnabled');
    applySimpleValue('multiPoseToggle');
    applySimpleValue('rubro');
    applySimpleValue('modoPro');
    applySimpleValue('creativoPrompt');

    persistState(base);
    return base;
  } catch (error) {
    console.warn('No se pudo actualizar el estado de crear anuncio:', error);
    return previous ?? null;
  }
};

export const clearLastCrearAnuncioState = () => {
  persistState(null);
};
